import React, { createContext, useState, useContext, useEffect } from 'react';

export const InterviewContext = createContext();

export const InterviewProvider = ({ children }) => {
    const [interviewData, setInterviewData] = useState({
        decision: null,      // User's initial decision (e.g., "ready", "help_choose")
        goal: null,           // Primary goal (e.g., "Lose weight")
        bestYouGoal: null,    // Best version goal (e.g., "Feel lighter and more confident (lose 5–20 lbs)")
        heightFeet: null,     // Height in feet
        heightInches: null,   // Height in inches
        weight: null,         // Weight in lbs
        bmi: null,           // Calculated BMI
        bmiCategory: null,   // BMI category (e.g., "Normal weight")
        priority: null,      // Priority (e.g., "affordability", "speed", "results")
        frequency: null,     // Payment frequency (e.g., "monthly", "quarterly")
        medication: null,    // Recommended medication (e.g., "semaglutide", "tirzepatide")
        formulation: null    // Formulation preference (e.g., "injectable", "suspension")
    });

    // Load data from localStorage on component mount
    useEffect(() => {
        const savedData = localStorage.getItem('tb2_interview_data');
        if (savedData) {
            try {
                const parsedData = JSON.parse(savedData);
                setInterviewData(parsedData);
            } catch (error) {
                console.error('Error loading interview data from localStorage:', error);
            }
        }
    }, []);

    // Save data to localStorage whenever interviewData changes
    useEffect(() => {
        localStorage.setItem('tb2_interview_data', JSON.stringify(interviewData));
    }, [interviewData]);

    const updateInterviewData = (field, value) => {
        setInterviewData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const updateMultipleFields = (fields) => {
        setInterviewData(prev => ({
            ...prev,
            ...fields
        }));
    };

    const clearInterviewData = () => {
        const emptyData = {
            decision: null,
            goal: null,
            bestYouGoal: null,
            heightFeet: null,
            heightInches: null,
            weight: null,
            bmi: null,
            bmiCategory: null,
            priority: null,
            frequency: null,
            medication: null
        };
        setInterviewData(emptyData);
        localStorage.removeItem('tb2_interview_data');
        console.log('Interview data cleared');
    };

    const getInterviewSummary = () => {
        return {
            ...interviewData,
            isComplete: !!(
                interviewData.goal &&
                interviewData.bestYouGoal &&
                interviewData.heightFeet &&
                interviewData.heightInches &&
                interviewData.weight &&
                interviewData.bmi &&
                interviewData.priority &&
                interviewData.frequency &&
                interviewData.medication
            )
        };
    };

    return (
        <InterviewContext.Provider value={{ 
            interviewData, 
            updateInterviewData, 
            updateMultipleFields,
            clearInterviewData,
            getInterviewSummary
        }}>
            {children}
        </InterviewContext.Provider>
    );
};

// Custom hook to use the interview context
export const useInterview = () => {
    const context = useContext(InterviewContext);
    if (context === undefined) {
        throw new Error('useInterview must be used within an InterviewProvider');
    }
    return context;
};
